import React from 'react';
    import { motion } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../common/SafeIcon';
    import Navigation from '../components/Navigation';
    import CosmicBackground from '../components/CosmicBackground';

    const { FiRocket, FiClock, FiPackage, FiTarget, FiArrowRight, FiCheck, FiStar, FiZap } = FiIcons;

    const LaunchKitsPage = () => {
      const kits = [
        {
          name: 'Creator Launch Kit',
          price: '$197',
          description: 'Perfect for content creators ready to monetize their audience',
          features: [
            'Pre-built sales funnels',
            'Email sequence templates',
            '5 landing page designs',
            'Social media templates',
            '48-hour setup',
          ],
          popular: false,
          link: 'https://link.fastpaydirect.com/payment-link/690b858b192c1d774336de40',
        },
        {
          name: 'Affiliate Pro Kit',
          price: '$297',
          description: 'Complete system for serious affiliate marketers',
          features: [
            'Advanced funnel sequences',
            'Conversion-optimized pages',
            'Email automation flows',
            'Tracking & analytics setup',
            'Bonus: Traffic generation guide',
          ],
          popular: true,
          link: 'https://link.fastpaydirect.com/payment-link/690b85a57b50c5515d11c6c0',
        },
        {
          name: 'Business Builder Kit',
          price: '$497',
          description: 'Full business-in-a-box for entrepreneurs',
          features: [
            'Complete business system',
            'Multi-product funnels',
            'Customer journey mapping',
            'Advanced automations',
            'Personal strategy session',
          ],
          popular: false,
          link: 'https://link.fastpaydirect.com/payment-link/690b85bb8a173d133270219c',
        },
      ];

      const testimonials = [
        {
          name: 'Rachel Kim',
          role: 'Online Coach',
          text: 'Launched my coaching business in 48 hours. Made $5K in the first week.',
          timeframe: '48 hours',
          rating: 5,
        },
        {
          name: 'Tom Wilson',
          role: 'Affiliate Marketer',
          text: 'The funnel templates converted at 23% right out of the box.',
          timeframe: '24 hours',
          rating: 5,
        },
      ];

      const process = [
        { step: '1', title: 'Choose Your Kit', description: 'Select the launch kit that matches your business goals' },
        { step: '2', title: 'Customize & Deploy', description: 'Our team helps you customize and deploy in 48 hours' },
        { step: '3', title: 'Launch & Scale', description: 'Go live with proven systems and start generating revenue' },
      ];

      const handleViewKitsClick = () => {
        document.getElementById('launch-kits-section')?.scrollIntoView({ behavior: 'smooth' });
      };

      return (
        <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
          <CosmicBackground />
          <Navigation />

          {/* Hero Section */}
          <section className="pt-32 pb-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-4xl mx-auto"
              >
                <div className="inline-flex items-center space-x-2 bg-orange-900/30 px-4 py-2 rounded-full border border-orange-500/30 mb-6">
                  <SafeIcon icon={FiRocket} className="text-orange-400" />
                  <span className="text-sm font-poppins text-orange-200">48-Hour Launch</span>
                </div>
                <h1 className="text-5xl md:text-7xl font-orbitron font-bold mb-6 leading-tight">
                  <span className="clarity-gradient">Launch Kits</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 mb-8 font-poppins font-light max-w-3xl mx-auto leading-relaxed">
                  Go from idea to income in 48 hours. Proven templates, systems, and funnels that eliminate months of
                  trial and error.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
                  <motion.button
                    onClick={handleViewKitsClick}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-orange-600 to-red-600 px-8 py-4 rounded-full font-poppins font-semibold text-lg glow-button flex items-center space-x-2"
                  >
                    <span>View Launch Kits</span>
                    <SafeIcon icon={FiArrowRight} />
                  </motion.button>
                  <div className="text-center">
                    <div className="flex justify-center items-center space-x-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current text-sm" />
                      ))}
                      <span className="ml-2 text-gray-400 text-sm">4.8/5 from 800+ launches</span>
                    </div>
                    <p className="text-gray-400 font-poppins text-sm">Average launch time: 36 hours</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-orange-900/40 to-red-900/40 p-6 rounded-2xl border border-orange-500/30 backdrop-blur-sm max-w-2xl mx-auto">
                  <div className="flex items-center justify-center space-x-3 mb-2">
                    <SafeIcon icon={FiClock} className="text-orange-400 text-xl" />
                    <span className="text-orange-300 font-poppins font-semibold">Ready to Launch in 48 Hours</span>
                  </div>
                  <p className="text-gray-300 font-poppins text-center text-sm">
                    Your complete business system will be live and ready to generate revenue within 48 hours.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Launch Kits */}
          <section id="launch-kits-section" className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Choose Your <span className="clarity-gradient">Launch Kit</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  Each kit is a one-time setup fee for a "done with you" service designed to accelerate your
                  Lumadious journey. We include everything you need to launch fast and scale smart.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {kits.map((kit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -10 }}
                    className={`relative p-8 rounded-2xl border backdrop-blur-sm transition-all duration-300 ${
                      kit.popular
                        ? 'bg-gradient-to-br from-orange-800/60 to-red-800/60 border-orange-400/50 hover:border-orange-400/70'
                        : 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/50 hover:border-orange-500/30'
                    }`}
                  >
                    {kit.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-1 rounded-full text-sm font-poppins font-semibold">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-orbitron font-bold text-white mb-2">{kit.name}</h3>
                      <div className="text-3xl font-poppins font-bold text-orange-400 mb-2">{kit.price}</div>
                      <p className="text-gray-300 font-poppins">{kit.description}</p>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {kit.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-300 font-poppins text-sm">
                          <SafeIcon
                            icon={FiCheck}
                            className={`mr-3 flex-shrink-0 ${kit.popular ? 'text-orange-400' : 'text-orange-500'}`}
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <a href={kit.link} target="_blank" rel="noopener noreferrer">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`w-full py-3 rounded-full font-poppins font-medium transition-all duration-300 ${
                          kit.popular
                            ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white glow-button hover:from-orange-600 hover:to-red-600'
                            : 'bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30 text-orange-300 hover:from-orange-600/40 hover:to-red-600/40'
                        }`}
                      >
                        {kit.popular ? 'Get Started Now' : 'Choose This Kit'}
                      </motion.button>
                    </a>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Process */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  How It <span className="clarity-gradient">Works</span>
                </h2>
              </motion.div>
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {process.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="text-white font-orbitron font-bold text-xl">{step.step}</span>
                    </div>
                    <h3 className="text-xl font-orbitron font-bold mb-4 text-white">{step.title}</h3>
                    <p className="text-gray-300 font-poppins">{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Success Stories */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Launch <span className="clarity-gradient">Success</span>
                </h2>
              </motion.div>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 p-8 rounded-2xl border border-gray-700/30 backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <div className="bg-gradient-to-r from-orange-600 to-red-600 px-3 py-1 rounded-full">
                        <span className="text-white font-poppins font-semibold text-sm">
                          Launched in {testimonial.timeframe}
                        </span>
                      </div>
                    </div>
                    <blockquote className="text-gray-300 font-poppins mb-6 italic">"{testimonial.text}"</blockquote>
                    <div>
                      <p className="text-white font-poppins font-semibold">{testimonial.name}</p>
                      <p className="text-gray-400 font-poppins text-sm">{testimonial.role}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center bg-gradient-to-r from-orange-900/40 to-red-900/40 p-12 rounded-3xl border border-orange-500/30 backdrop-blur-sm max-w-4xl mx-auto"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Ready to <span className="clarity-gradient">Launch</span> Fast?
                </h2>
                <p className="text-xl text-gray-300 font-poppins mb-8 max-w-2xl mx-auto">
                  Stop planning and start earning. Your business can be live in 48 hours.
                </p>
                <motion.button
                  onClick={handleViewKitsClick}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-orange-600 to-red-600 px-12 py-4 rounded-full font-poppins font-semibold text-xl glow-button"
                >
                  Choose Your Launch Kit
                </motion.button>
              </motion.div>
            </div>
          </section>
        </div>
      );
    };

    export default LaunchKitsPage;